import { useState, useEffect, useRef } from 'react'
import { Button, Form, FormGroup, Label, Input, FormFeedback, Alert } from 'reactstrap'
import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { CombinedGraphQLErrors } from '@apollo/client'
import Spinner from '../Spinner/Spinner'
import { deepCopy, passwordErrors } from '../../utils'
import { SIGN_UP_MUTATION } from '../../graphql.js'
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai'
import MessageCard from '../MessageCard/MessageCard.js'
import ReCAPTCHA from 'react-google-recaptcha'

const SIGN_UP = gql(SIGN_UP_MUTATION)

export default function SignUp() {
  console.log('SignUp rendered')

  const [inputs, setInputs] = useState({ email: '', name: '', password: '', cellPhone: '' })
  const [captchaToken, setCaptchaToken] = useState()
  const [emailValid, setEmailValid] = useState({ emailValid: null, emailFeedback: '' })
  const [nameValid, setNameValid] = useState({ nameValid: null, nameFeedback: '' })
  const [passwordValid, setPasswordValid] = useState({ passwordValid: null, passwordFeedback: '' })
  const [cellPhoneValid, setCellPhoneValid] = useState({ cellPhoneValid: null, cellPhoneFeedback: '' })
  const [signUpError, setSignUpError] = useState()
  const [signUpMutation, { loading: mutationLoading, error: mutationError }] = useMutation(SIGN_UP)
  const [visible, setVisible] = useState(false)
  const [success, setSuccess] = useState(false)
  const iconStyle = {
    fontSize: 'large',
  }

  // focus on the email input after render
  const emailInput = useRef(null)
  useEffect(() => {
    emailInput.current.focus()
    document.title = 'Sign Up'
  }, [])

  // create ref for reCAPTCHA
  const recaptcha = useRef()

  function onChange(value) {
    console.log('reCAPTCHA onChange value: ', value)
    setCaptchaToken(value)
  }

  function handleInputChange(event) {
    const newInputs = deepCopy(inputs)
    newInputs[event.target.name] = event.target.value
    setInputs(newInputs)
    return true
  }

  async function handleSignUp() {
    console.log('handleSignUp: ', inputs)

    setSignUpError(null)
    let inputErrorCount = 0

    // reCAPTCHA
    console.log('SignUp.handleSignUp captchaValue: ', captchaToken)
    if (!captchaToken) {
      inputErrorCount += 1
      setSignUpError('Please verify the reCAPTCHA!')
    }

    // Email validation
    if (!inputs.email) {
      setEmailValid({ emailValid: false, emailFeedback: 'Email is required' })
      inputErrorCount += 1
    } else {
      if (inputs.email.indexOf('@') === -1 || inputs.email.indexOf('.') === -1) {
        setEmailValid({ emailValid: false, emailFeedback: 'not a valid email address' })
        inputErrorCount += 1
      } else {
        setEmailValid({ emailValid: null, emailFeedback: '' })
      }
    }
    // Name validation
    if (!inputs.name) {
      setNameValid({ nameValid: false, nameFeedback: 'Full Name is required' })
      inputErrorCount += 1
    } else {
      if (!inputs.name.includes(' ')) {
        setNameValid({ nameValid: false, nameFeedback: 'First and last name is required' })
        inputErrorCount += 1
      } else {
        setNameValid({ nameValid: null, nameFeedback: '' })
      }
    }
    // Password validation
    if (!inputs.password) {
      setPasswordValid({ passwordValid: false, passwordFeedback: 'Password is required' })
      inputErrorCount += 1
    } else {
      const errorTextArray = passwordErrors(inputs.password)
      if (errorTextArray) {
        let passwordFeedback = ''
        for (let errorText of errorTextArray) {
          passwordFeedback += errorText + '. '
        }
        setPasswordValid({ passwordValid: false, passwordFeedback: passwordFeedback })
      } else {
        setPasswordValid({ passwordValid: null, passwordFeedback: '' })
      }
    }
    // Cell phone validation
    if (inputs.cellPhone) {
      const number = inputs.cellPhone.replaceAll('-', '').replaceAll('(', '').replaceAll(')', '')
      if (isNaN(number)) {
        setCellPhoneValid({ cellPhoneValid: false, cellPhoneFeedback: 'Not a valid mobile number' })
        inputErrorCount += 1
      } else {
        if (number.length !== 10) {
          setCellPhoneValid({ cellPhoneValid: false, cellPhoneFeedback: 'Mobile number must be 10 digits' })
          inputErrorCount += 1
        } else {
          setCellPhoneValid({ cellPhoneValid: null, cellPhoneFeedback: '' })
        }
      }
    } else {
      setCellPhoneValid({ cellPhoneValid: null, cellPhoneFeedback: '' })
    }
    if (inputErrorCount === 0) {
      inputs.host = window.location.origin
      inputs.captchaToken = captchaToken
      console.log('SignUp.handleSignUp inputs: ', inputs)
      await signUpMutation({ variables: inputs })
        .then((res) => {
          console.log('signUpMutation result:', res)
          if (res.data.signup === 'SUCCESS') {
            setSuccess(true)
          }
        })
        .catch((error) => {
          if (CombinedGraphQLErrors.is(error)) {
            const gqlErrors = error.errors.map((gqlError) => {
              return gqlError.message
            })
            console.log(gqlErrors)
            setSignUpError(gqlErrors[0])
            if (gqlErrors[0].indexOf('password') >= 0) {
              setPasswordValid({ passwordValid: false, passwordFeedback: gqlErrors[0] })
              setEmailValid({ emailValid: true, emailFeedback: null })
            }
            if (gqlErrors[0].indexOf('email') >= 0) {
              setEmailValid({ emailValid: false, emailFeedback: gqlErrors[0] })
              setPasswordValid({ passwordValid: null, passwordFeedback: null })
            }
          }
        })
    }
  }
  
  let buttonComponent = 
    <Button 
      color="primary" onClick={handleSignUp} block data-cy="signUp-button"
      style={{ width: '100%', fontSize: '1.25rem', marginTop: '15px'}}>
      Next
    </Button>
  
  if (mutationLoading) {
    buttonComponent = <Spinner />
  }

  if (success) {
    return (
      <MessageCard
        title="New Account Request Submitted"
        message={`An email has been sent to ${inputs.email} for verification.`}
        buttonText="Go To Login"
        clickUrl="/login"
      />
    )
  }

  return (
    <div className="max-width-panel" >
      <div className="panel panel-default bootstrap-basic">
        <div className="panel-heading">
          <div className="text-center">
            <h1 style={{ paddingTop: '5px', marginBottom: '10px', fontSize: '24px', fontWeight: '300', letterSpacing: '-0.5px' }}>
              Create An Account
            </h1>
            <Alert color="danger" isOpen={signUpError ? true : false || mutationError ? true : false}>
              {signUpError || (mutationError && mutationError.message)}
            </Alert>
          </div>
        </div>
        <Form className="panel-body">
          <FormGroup>
            <Label for="signUp-email">Email</Label>
            <Input
              innerRef={emailInput}
              type="text"
              name="email"
              value={inputs.email}
              invalid={emailValid.emailValid === false}
              valid={emailValid.emailValid === true}
              onChange={handleInputChange}
              data-cy="signUp-email"
              autoComplete="username"
            />
            <FormFeedback>
              {emailValid.emailFeedback}
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="signUp-name">Full Name</Label>
            <Input
              type="text"
              name="name"
              value={inputs.name}
              invalid={nameValid.nameValid === false}
              valid={nameValid.nameValid === true}
              onChange={handleInputChange}
              data-cy="signUp-name"
              autoComplete="current-name"
            />
            <FormFeedback>
              {nameValid.nameFeedback}
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="signUp-password">Password</Label>
            <div style={{ position: 'relative' }}>
              <Input
                type={visible ? 'text' : 'password'}
                name="password"
                value={inputs.password}
                invalid={passwordValid.passwordValid === false}
                valid={passwordValid.passwordValid === true}
                onChange={handleInputChange}
                data-cy="signUp-password"
                autoComplete="current-password"
                style={{ display: 'inline-block' }}
              />
              <FormFeedback>
                {passwordValid.passwordFeedback}
              </FormFeedback>
              <div onClick={() => setVisible(!visible)} style={{ display: 'inline-block', position: 'absolute', right: '5px', top: '5px' }}>
                {visible ? <AiOutlineEyeInvisible style={iconStyle}/> : <AiOutlineEye style={iconStyle} />}
              </div>
            </div>
          </FormGroup>
          <FormGroup>
            <Label for="signUp-cellPhone">Mobile Number</Label>
            <Input
              type="text"
              name="cellPhone"
              value={inputs.cellPhone}
              invalid={cellPhoneValid.cellPhoneValid === false}
              valid={cellPhoneValid.cellPhoneValid === true}
              onChange={handleInputChange}
              data-cy="signUp-cellPhone"
              autoComplete="current-cellPhone"
            />
            <FormFeedback>
              {cellPhoneValid.cellPhoneFeedback}
            </FormFeedback>
          </FormGroup>
          <ReCAPTCHA ref={recaptcha} onChange={onChange} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY} />
          {buttonComponent}
        </Form>
      </div>
    </div>
  )
}